import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Row, Col, Card, Badge, Button, Alert, Spinner, Modal } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import * as GastosAPI from "../../api/gastos.api.ts";
import Swal from 'sweetalert2';

export default function GastoDetalleViewPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const [gasto, setGasto] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [eliminando, setEliminando] = useState(false);

    const cargarGasto = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await GastosAPI.getGasto(id);
            
            if (response.success && response.data) {
                setGasto(response.data);
            } else {
                setError('No se pudo cargar la información del gasto');
            }
        } catch (err) {
            console.error('Error cargando gasto:', err);
            setError('Error al cargar los datos del gasto');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        cargarGasto();
    }, [cargarGasto]);

    // Mostrar loader mientras carga
    if (loading) {
        return (
            <PageLayout>
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </Spinner>
                </div>
            </PageLayout>
        );
    }

    // Mostrar error si no se encontró el gasto
    if (error || !gasto) {
        return (
            <PageLayout>
                <Alert variant="warning">
                    <Alert.Heading>Gasto no encontrado</Alert.Heading>
                    <p>{error || 'El gasto solicitado no existe o no tienes permisos para verlo.'}</p>
                    <hr />
                    <div className="d-flex justify-content-end">
                        <Button variant="outline-warning" onClick={() => navigate('/gastos')}>
                            Volver a Gastos
                        </Button>
                    </div>
                </Alert>
            </PageLayout>
        );
    }

    const formatCurrency = (amount) => {
        const numAmount = typeof amount === 'string' ? 
            parseFloat(amount) : amount;
        
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(numAmount);
    };

    const getStatusBadge = (estado) => {
        const statusConfig = {
            'APROBADO': { bg: 'success', text: 'Aprobado' },
            'PENDIENTE': { bg: 'warning', text: 'Pendiente' },
            'RECHAZADO': { bg: 'danger', text: 'Rechazado' },
            'BORRADOR': { bg: 'secondary', text: 'Borrador' }
        };
        
        const config = statusConfig[estado] || { bg: 'secondary', text: estado };
        return <Badge bg={config.bg} className="fs-6">{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'No especificada';
        
        const date = new Date(dateString);
        return date.toLocaleDateString('es-CL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const imprimirComprobante = () => {
        // Crear una nueva ventana con el comprobante
        const ventanaImpresion = window.open('', '_blank', 'width=800,height=600');
        
        const contenidoComprobante = `
            <!DOCTYPE html>
            <html lang="es">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Comprobante de Gasto - ${gasto.gas_codigo}</title>
                <style>
                    body {
                        font-family: 'Arial', sans-serif;
                        margin: 20px;
                        color: #333;
                        line-height: 1.6;
                    }
                    .header {
                        text-align: center;
                        border-bottom: 3px solid #007bff;
                        padding-bottom: 20px;
                        margin-bottom: 30px;
                    }
                    .header h1 {
                        color: #007bff;
                        margin: 0;
                        font-size: 28px;
                    }
                    .header h2 {
                        color: #666;
                        margin: 5px 0 0 0;
                        font-size: 18px;
                        font-weight: normal;
                    }
                    .comprobante-info {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 30px;
                        background-color: #f8f9fa;
                        padding: 15px;
                        border-radius: 8px;
                    }
                    .info-section {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 20px;
                        margin-bottom: 30px;
                    }
                    .info-group {
                        border: 1px solid #e9ecef;
                        padding: 15px;
                        border-radius: 6px;
                        background-color: #fff;
                    }
                    .info-label {
                        font-weight: bold;
                        color: #495057;
                        font-size: 12px;
                        text-transform: uppercase;
                        margin-bottom: 5px;
                        display: block;
                    }
                    .info-value {
                        font-size: 14px;
                        color: #212529;
                    }
                    .monto-section {
                        text-align: center;
                        background: linear-gradient(135deg, #28a745, #20c997);
                        color: white;
                        padding: 20px;
                        border-radius: 10px;
                        margin: 30px 0;
                    }
                    .monto-value {
                        font-size: 36px;
                        font-weight: bold;
                        margin: 0;
                    }
                    .monto-label {
                        font-size: 16px;
                        opacity: 0.9;
                        margin: 5px 0 0 0;
                    }
                    .status-badge {
                        display: inline-block;
                        padding: 6px 12px;
                        border-radius: 20px;
                        font-size: 12px;
                        font-weight: bold;
                        text-transform: uppercase;
                    }
                    .status-aprobado { background-color: #d4edda; color: #155724; }
                    .status-pendiente { background-color: #fff3cd; color: #856404; }
                    .status-rechazado { background-color: #f8d7da; color: #721c24; }
                    .status-borrador { background-color: #e2e3e5; color: #383d41; }
                    .footer {
                        margin-top: 40px;
                        text-align: center;
                        font-size: 12px;
                        color: #6c757d;
                        border-top: 1px solid #e9ecef;
                        padding-top: 20px;
                    }
                    .categoria-indicator {
                        width: 20px;
                        height: 20px;
                        border-radius: 4px;
                        display: inline-block;
                        margin-right: 8px;
                        vertical-align: middle;
                    }
                    @media print {
                        body { margin: 0; }
                        .no-print { display: none; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>COMPROBANTE DE GASTO</h1>
                    <h2>Sistema de Gestión Estugira</h2>
                </div>

                <div class="comprobante-info">
                    <div>
                        <strong>Código:</strong> ${gasto.gas_codigo}<br>
                        <strong>Estado:</strong> 
                        <span class="status-badge status-${gasto.gas_estado.toLowerCase()}">
                            ${gasto.gas_estado}
                        </span>
                    </div>
                    <div style="text-align: right;">
                        <strong>Fecha de emisión:</strong><br>
                        ${new Date().toLocaleDateString('es-CL', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </div>
                </div>

                <div class="monto-section">
                    <p class="monto-value">${formatCurrency(gasto.gas_monto)}</p>
                    <p class="monto-label">Monto del Gasto</p>
                </div>

                <div class="info-section">
                    <div class="info-group">
                        <span class="info-label">Descripción</span>
                        <div class="info-value">${gasto.gas_descripcion}</div>
                    </div>
                    
                    <div class="info-group">
                        <span class="info-label">Responsable</span>
                        <div class="info-value">${gasto.gas_responsable}</div>
                    </div>
                    
                    <div class="info-group">
                        <span class="info-label">Categoría</span>
                        <div class="info-value">
                            <span class="categoria-indicator" style="background-color: ${gasto.categoria_color || '#6c757d'};"></span>
                            ${gasto.categoria_nombre}
                        </div>
                    </div>
                    
                    <div class="info-group">
                        <span class="info-label">Subcategoría</span>
                        <div class="info-value">${gasto.subcategoria_nombre}</div>
                    </div>
                    
                    <div class="info-group">
                        <span class="info-label">Tipo de Gasto</span>
                        <div class="info-value">${gasto.tipo_nombre || 'No especificado'}</div>
                    </div>
                    
                    <div class="info-group">
                        <span class="info-label">Fondo</span>
                        <div class="info-value">${gasto.fondo_nombre || 'Sin fondo asignado'}</div>
                    </div>
                    
                    <div class="info-group">
                        <span class="info-label">Fecha del Gasto</span>
                        <div class="info-value">${formatDate(gasto.gas_fecha_gasto)}</div>
                    </div>
                    
                    <div class="info-group">
                        <span class="info-label">Año Presupuesto</span>
                        <div class="info-value">${gasto.gas_anio_presupuesto}</div>
                    </div>
                </div>

                ${gasto.gas_observaciones ? `
                    <div class="info-group" style="grid-column: 1 / -1;">
                        <span class="info-label">Observaciones</span>
                        <div class="info-value">${gasto.gas_observaciones}</div>
                    </div>
                ` : ''}

                <div class="footer">
                    <p>Este comprobante es un documento informativo generado automáticamente por el sistema.</p>
                    <p>Fecha de creación del gasto: ${formatDate(gasto.gas_fecha_creacion)}</p>
                </div>

                <div class="no-print" style="text-align: center; margin-top: 30px;">
                    <button onclick="window.print()" style="
                        background-color: #007bff;
                        color: white;
                        border: none;
                        padding: 12px 24px;
                        border-radius: 6px;
                        font-size: 16px;
                        cursor: pointer;
                        margin-right: 10px;
                    ">Imprimir</button>
                    <button onclick="window.close()" style="
                        background-color: #6c757d;
                        color: white;
                        border: none;
                        padding: 12px 24px;
                        border-radius: 6px;
                        font-size: 16px;
                        cursor: pointer;
                    ">Cerrar</button>
                </div>
            </body>
            </html>
        `;
        
        ventanaImpresion.document.write(contenidoComprobante);
        ventanaImpresion.document.close();
        
        // Auto-enfocar la nueva ventana
        ventanaImpresion.focus();
    };

    // Función para verificar si el gasto se puede eliminar
    const puedeEliminar = () => {
        if (!gasto) return false;
        const estado = gasto.gas_estado?.toLowerCase() || '';
        return estado === 'pendiente' || estado === 'borrador';
    };

    // Función para eliminar el gasto
    const eliminarGasto = async () => {
        try {
            const result = await Swal.fire({
                title: '¿Eliminar Gasto?',
                html: `
                    <div class="text-start">
                        <p>¿Está seguro que desea eliminar el siguiente gasto?</p>
                        <div class="alert alert-info">
                            <strong>Código:</strong> ${gasto.gas_codigo}<br/>
                            <strong>Descripción:</strong> ${gasto.gas_descripcion}<br/>
                            <strong>Monto:</strong> ${formatCurrency(gasto.gas_monto)}
                        </div>
                        <p class="text-danger">
                            <i class="fas fa-exclamation-triangle"></i>
                            Esta acción no se puede deshacer.
                        </p>
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#dc3545',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Sí, Eliminar',
                cancelButtonText: 'Cancelar',
                focusCancel: true
            });

            if (result.isConfirmed) {
                setEliminando(true);
                
                const response = await GastosAPI.eliminarGasto(gasto.gas_id);
                
                if (response.success) {
                    await Swal.fire({
                        title: '¡Eliminado!',
                        text: 'El gasto ha sido eliminado exitosamente.',
                        icon: 'success',
                        confirmButtonColor: '#28a745'
                    });
                    
                    // Redirigir a la lista de gastos
                    navigate('/gastos');
                } else {
                    await Swal.fire({
                        title: 'Error',
                        text: response.message || 'No se pudo eliminar el gasto',
                        icon: 'error',
                        confirmButtonColor: '#dc3545'
                    });
                }
            }
        } catch (error) {
            console.error('Error al eliminar gasto:', error);
            await Swal.fire({
                title: 'Error',
                text: 'Error al conectar con el servidor',
                icon: 'error',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setEliminando(false);
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Detalle del Gasto</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='/gastos' className="mc-breadcrumb-link">Gastos</Link></li>
                                <li className="mc-breadcrumb-item">Ver Gasto</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Información principal */}
                <Col xl={8}>
                    <Card className="mc-card">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h4>Información del Gasto</h4>
                            <div>
                                {getStatusBadge(gasto.gas_estado)}
                                <span className="badge bg-light text-dark ms-2">{gasto.gas_codigo}</span>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={12} className="mb-4">
                                    <div className="border-start border-4 ps-3" 
                                         style={{ borderColor: `${gasto.categoria_color || '#6c757d'} !important` }}>
                                        <h5 className="mb-1">{gasto.gas_descripcion}</h5>
                                        <p className="text-muted mb-0">{gasto.gas_observaciones || 'Sin observaciones'}</p>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Categoría</label>
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="me-2" 
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    backgroundColor: gasto.categoria_color || '#6c757d',
                                                    borderRadius: '3px'
                                                }}
                                            ></div>
                                            <span>{gasto.categoria_nombre}</span>
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Subcategoría</label>
                                        <div>{gasto.subcategoria_nombre}</div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Tipo de Gasto</label>
                                        <div>{gasto.tipo_nombre || 'No especificado'}</div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Fondo</label>
                                        <div>{gasto.fondo_nombre || 'Sin fondo asignado'}</div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Responsable</label>
                                        <div>{gasto.gas_responsable}</div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Fecha del Gasto</label>
                                        <div>{formatDate(gasto.gas_fecha_gasto)}</div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Fecha de Creación</label>
                                        <div>{formatDate(gasto.gas_fecha_creacion)}</div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Año Presupuesto</label>
                                        <div>{gasto.gas_anio_presupuesto}</div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Panel lateral */}
                <Col xl={4}>
                    <Card className="mc-card">
                        <Card.Header>
                            <h5>Resumen Financiero</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="text-center mb-4">
                                <h2 className="text-success">{formatCurrency(gasto.gas_monto)}</h2>
                                <p className="text-muted">Monto del Gasto</p>
                            </div>

                            <div className="d-grid gap-2">
                                <Button 
                                    variant="primary" 
                                    as={Link} 
                                    to={`/gastos/editar/${gasto.gas_id}`}
                                >
                                    <i className="fas fa-edit me-2"></i>
                                    Editar Gasto
                                </Button>
                                
                                <Button 
                                    variant="success" 
                                    onClick={imprimirComprobante}
                                >
                                    <i className="fas fa-print me-2"></i>
                                    Imprimir Comprobante
                                </Button>
                                
                                {puedeEliminar() && (
                                    <Button 
                                        variant="danger" 
                                        onClick={eliminarGasto}
                                        disabled={eliminando}
                                    >
                                        {eliminando ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Eliminando...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-trash me-2"></i>
                                                Eliminar Gasto
                                            </>
                                        )}
                                    </Button>
                                )}
                                
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => navigate('/gastos')}
                                >
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Volver a Gastos
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Documento adjunto si existe */}
                    {gasto.gas_documento_adjunto && (
                        <Card className="mc-card mt-3">
                            <Card.Header>
                                <h5>Documento Adjunto</h5>
                            </Card.Header>
                            <Card.Body>
                                <Button 
                                    variant="outline-info" 
                                    href={gasto.gas_documento_adjunto} 
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Ver Documento
                                </Button>
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>
        </PageLayout>
    );
}